import { CbhIcon, type CbhIconProps } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import {
  // eslint-disable-next-line no-restricted-imports
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  ThemeProvider,
} from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { BaseLink } from "@src/appV2/lib";
import { useIsJobsEnabled } from "@src/appV2/redesign/PlacementCandidate/useIsJobsEnabled";
import { SHIFT_DISCOVERY_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { type ReactElement } from "react";
import { useLocation } from "react-router-dom";

import { useGetChatChannels } from "../Chat/ChatList/hooks/useGetChatChannels";
import { CHAT_LIST_FULL_PATH } from "../Chat/paths";
import { useEmbeddedMode } from "../EmbeddedMode/EmbeddedModeContext";
import { CbhFeatureFlag } from "../FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "../FeatureFlags/useCbhFlag";
import { useIsNewsFeedEnabled } from "../NewsFeed/api/useIsNewsFeedEnabled";
import { NEWS_FEED_FULL_PATH } from "../NewsFeed/paths";
import { ACCOUNTS_BASE_PATH } from "./Accounts/paths";
import { useShouldShowJobsFirst } from "./Onboarding/hooks/useShouldShowJobsFirst";
import { BadgedJobsIcon } from "./Placements/components/BadgedJobsIcon";
import { PLACEMENT_PATHS } from "./Placements/paths";
import { isRebrandingEnabled } from "./Rebranding/workerAppRebranding";
import { FEED_FULL_PATH, SOCIAL_FULL_PATH } from "./Social/paths";
import { MY_SHIFTS_FULL_PATH } from "./WorkerShifts/path";

function getChatColoredIcon(): CbhIconProps["type"] {
  return isRebrandingEnabled() ? "rebrand-chat-colored" : "chat-colored";
}

function getClipboardWithNotesColoredIcon(): CbhIconProps["type"] {
  return isRebrandingEnabled()
    ? "rebrand-clipboard-with-notes-colored"
    : "clipboard-with-notes-colored";
}

function getBriefcaseColoredIcon(): "briefcase-colored" | "rebrand-briefcase-colored" {
  return isRebrandingEnabled() ? "rebrand-briefcase-colored" : "briefcase-colored";
}

function getSearchColoredIcon(): CbhIconProps["type"] {
  return isRebrandingEnabled() ? "rebrand-search-colored" : "search-colored";
}

function getUserColoredIcon(): CbhIconProps["type"] {
  return isRebrandingEnabled() ? "rebrand-user-colored" : "user-colored";
}

export function Navbar(): ReactElement {
  const location = useLocation();

  const { isEmbedded } = useEmbeddedMode();
  const isJobsEnabled = useIsJobsEnabled();
  const isNewsFeedEnabled = useIsNewsFeedEnabled();

  const theme = getTheme();
  const { data: chatChannelsData } = useGetChatChannels({
    refetchInterval: (data) => {
      /* We need to wait until the SDK is initialized, but SendBird does not provide
       * any out-of-the-box way to wait and perform operations after initialization. Hence
       * we're using an immediate refetching as a workaround which solves the issue. We're
       * using the lowest non-zero interval to refetch which is 1 ms. We're using this to
       * show the badge for unread chat messages in the navbar. Note that if we swap this
       * out with 'retries' instead, for some reason the SDK never initializes.
       */
      if (!data?.isSdkInitialized) {
        return 1;
      }

      return 0;
    },
  });

  const isVideoPostingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_VIDEO_POSTING, {
    defaultValue: false,
  });

  const unreadChatsCount = (chatChannelsData?.channels ?? [])
    .map((channel) => channel.unreadMessageCount)
    .reduce((accumulator, count) => accumulator + count, 0);

  const hasUnreadChats = unreadChatsCount > 0;

  const showJobsFirst = useShouldShowJobsFirst();

  const shiftsPath = `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_PATH}`;

  let currentLocation = location.pathname;

  // make sure we include all nested paths for shift discovery so navigation is properly selected
  // example: /shift-discovery/list and /shift-discovery/map should both be treated as /shift-discovery
  // TODO: This needs a rework so it works for all other root paths like account, etc
  if (currentLocation.startsWith(shiftsPath)) {
    currentLocation = shiftsPath;
  }

  // same for placements
  if (currentLocation.startsWith(PLACEMENT_PATHS.ROOT)) {
    currentLocation = PLACEMENT_PATHS.ROOT;
  }

  return (
    <ThemeProvider theme={theme}>
      <BottomNavigation showLabels value={currentLocation}>
        {isJobsEnabled && showJobsFirst && (
          <BottomNavigationAction
            component={BaseLink}
            to={PLACEMENT_PATHS.ROOT}
            value={PLACEMENT_PATHS.ROOT}
            icon={
              <BadgedJobsIcon
                type={
                  currentLocation === PLACEMENT_PATHS.ROOT ? getBriefcaseColoredIcon() : "briefcase"
                }
                size="large"
              />
            }
            label="Jobs"
            id="jobs"
          />
        )}
        {!isEmbedded && (
          <BottomNavigationAction
            component={BaseLink}
            to={shiftsPath}
            value={shiftsPath}
            icon={
              <CbhIcon
                type={currentLocation === shiftsPath ? getSearchColoredIcon() : "search"}
                size="large"
              />
            }
            label="Shifts"
            id="shifts"
          />
        )}
        {isJobsEnabled && !showJobsFirst && (
          <BottomNavigationAction
            component={BaseLink}
            to={PLACEMENT_PATHS.ROOT}
            value={PLACEMENT_PATHS.ROOT}
            icon={
              <BadgedJobsIcon
                type={
                  currentLocation === PLACEMENT_PATHS.ROOT ? getBriefcaseColoredIcon() : "briefcase"
                }
                size="large"
              />
            }
            label="Jobs"
            id="jobs"
          />
        )}
        {!isEmbedded && (
          <BottomNavigationAction
            component={BaseLink}
            to={MY_SHIFTS_FULL_PATH}
            value={MY_SHIFTS_FULL_PATH}
            icon={
              <CbhIcon
                type={
                  currentLocation === MY_SHIFTS_FULL_PATH
                    ? getClipboardWithNotesColoredIcon()
                    : "clipboard-with-notes"
                }
                size="large"
              />
            }
            label="Bookings"
            id="bookings"
          />
        )}
        <BottomNavigationAction
          component={BaseLink}
          to={CHAT_LIST_FULL_PATH}
          value={CHAT_LIST_FULL_PATH}
          icon={
            <Badge
              variant="dot"
              invisible={!hasUnreadChats}
              color="primary"
              sx={(theme) => ({
                "& .MuiBadge-dot": {
                  backgroundColor: theme.palette.error.main,
                },
              })}
            >
              <CbhIcon
                type={currentLocation === CHAT_LIST_FULL_PATH ? getChatColoredIcon() : "chat"}
                size="large"
              />
            </Badge>
          }
          label="Chat"
          id="chat"
        />
        {isVideoPostingEnabled && (
          <BottomNavigationAction
            component={BaseLink}
            to={FEED_FULL_PATH}
            value={FEED_FULL_PATH}
            icon={
              <CbhIcon
                type={
                  currentLocation.includes(SOCIAL_FULL_PATH)
                    ? "cbh-bunny-colored"
                    : "clipboard-bunny-colored"
                }
                size="large"
              />
            }
            label="Social"
            id="social"
          />
        )}
        {isNewsFeedEnabled && (
          <BottomNavigationAction
            component={BaseLink}
            to={NEWS_FEED_FULL_PATH}
            value={NEWS_FEED_FULL_PATH}
            icon={
              <Badge color="info">
                <CbhIcon type="patients" size="large" />
              </Badge>
            }
            label="NewsFeed"
            id="newsfeed"
          />
        )}
        <BottomNavigationAction
          component={BaseLink}
          to={ACCOUNTS_BASE_PATH}
          value={ACCOUNTS_BASE_PATH}
          icon={
            <CbhIcon
              type={currentLocation === ACCOUNTS_BASE_PATH ? getUserColoredIcon() : "user"}
              size="large"
            />
          }
          label="Account"
          id="account"
        />
      </BottomNavigation>
    </ThemeProvider>
  );
}
